<script setup>
import { computed } from 'vue';

import { useRouter } from 'vue-router';

import { Settings, CreditCard, Bell, LogOut, User } from 'lucide-vue-next';

import { can } from '@/@layouts/plugins/casl';
import { useAuthStore } from '@/stores/auth';

const props = defineProps({
  user: {
    type: Object,
  },
});
const emit = defineEmits(['close']);
const authStore = useAuthStore();
const router = useRouter();

const userEmail = computed(() => props.user?.email);
const userId = computed(() => props.user?.sub);

const loginProviderLabel = computed(() => {
  const u = props.user || {};

  // Primary login provider from /users/me (enum: GOOGLE / AMAZON_LOGIN / COGNITO / APPLE / FACEBOOK).
  // Fall back to the value stashed during the OAuth callback if the profile hasn't loaded yet.
  const provider = u.provider || localStorage.getItem('last-auth-provider');

  const p = String(provider || '').toLowerCase();
  if (!p) return '';
  if (p.includes('google')) return 'Google';
  if (p.includes('amazon')) return 'Amazon';
  if (p.includes('apple')) return 'Apple';
  if (p.includes('facebook')) return 'Facebook';
  if (p.includes('cognito') || p.includes('email') || p.includes('form')) return 'Form';
  return p.charAt(0).toUpperCase() + p.slice(1);
});

const logoutAction = async () => {
  await authStore.logout();
};
</script>

<template>
  <div
    class="user"
    @click.stop
  >
    <div class="user-info">
      <span class="text-blur user-info-mail">{{ $t('Email') }}: <span v-no-translate>{{ userEmail }}</span></span>
      <span class="text-blur user-info-id">{{ $t('ID') }}: <span v-no-translate>{{ userId }}</span></span>
      <span v-if="loginProviderLabel" class="text-blur user-info-id">{{ $t('Provider') }}: {{ loginProviderLabel }}</span>
    </div>
    <VDivider />
    <ul class="user-info-menu">
      <li class="user-info-menu-item">
        <div
          class="user-link"
          @click="(router.push({ name: 'settings-profile' }), emit('close'))"
        >
          <User :size="16" />
          <span>{{ $t('My Profile') }}</span>
        </div>
      </li>
      <li
        v-if="can('read', 'Settings')"
        class="user-info-menu-item"
      >
        <div
          class="user-link"
          @click="(router.push('/settings/connections'), emit('close'))"
        >
          <Settings :size="16" />
          <span>{{ $t('Settings') }}</span>
        </div>
      </li>
      <li class="user-info-menu-item">
        <div
          class="user-link"
          @click="(router.push('/billing'), emit('close'))"
        >
          <CreditCard :size="16" />
          <span>{{ $t('Plans & Billing') }}</span>
        </div>
      </li>
      <li class="user-info-menu-item disabled">
        <div class="user-link">
          <Bell :size="16" />
          <span>{{ $t('Notification Settings') }}</span>
        </div>
      </li>
    </ul>
    <VDivider />
    <div class="user-footer">
      <div
        variant="outlined"
        class="user-link"
        @click="logoutAction"
      >
        <LogOut :size="16" />
        <span>{{ $t('Log Out') }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user {
  z-index: 100;
  border-radius: 8px;
  width: 240px;
  padding: 16px 6px;
  cursor: default;

  &__main-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    gap: var(--gap);
  }

  &__avatar {
    overflow: hidden;
    border-radius: 50%;
    width: 64px;
    height: 64px;

    img {
      object-fit: contain;
    }

    &--default {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      background: var(--wp-surface-bg-icon);

      .svg {
        width: 35px;
        height: 35px;
      }
    }
  }

  &__info {
    max-width: calc(100% - 80px);
  }

  &__name {
    overflow: hidden;
    margin-bottom: 8px;
    font-size: 1.25rem;
    line-height: 1.5rem;
    text-overflow: ellipsis;
    text-align: left;
    color: var(--wp-text-primary);
    text-transform: capitalize;
  }

  &__status {
    width: fit-content;
  }
}

.user-info {
  margin-bottom: 16px;
  // The popup is rendered inside a native <button>, which disables text
  // selection for its whole subtree via the UA stylesheet. Re-enable it here
  // so the email / ID / provider values can be selected and copied.
  -webkit-user-select: text;
  user-select: text;
  cursor: text;

  &-mail {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    color: var(--wp-text-primary);
    display: block;
    width: 100%;
    margin-bottom: 4px;
  }

  &-id {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    color: var(--wp-text-primary);
    display: block;
    width: 100%;
  }

  &-provider {
    color: var(--wp-text-disabled);
  }

  &-menu {
    margin-top: 16px;
    margin-bottom: 16px;
  }
}

.user-link {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  cursor: pointer;

  & svg {
    width: 16px;
    height: 16px;
    block-size: 16px;
    inline-size: 16px;
    min-width: 16px;
    max-width: 16px;

    path {
      fill: var(--wp-text-primary);
    }
  }

  &:hover {
    border-radius: 8px;
    color: var(--wp-text-secondary);
    background: var(--wp-state-hover-menu);

    & svg {
      path {
        fill: var(--wp-text-secondary);
      }
    }

    .v-icon {
      color: var(--wp-text-secondary);
    }
  }

  .v-icon {
    color: var(--wp-text-primary);
  }
}

.user-footer {
  margin-top: 16px;
  color: var(--wp-text-primary);
}

.v-application__wrap .v-divider {
  width: calc(100% - 40px);
  margin: 0 auto;
}

.user-info-menu-item {
  color: var(--wp-text-primary);
}

.user-info-menu-item.disabled {
  color: var(--wp-text-disabled);

  svg path {
    fill: var(--wp-text-disabled);
  }

  .user-link:hover {
    background: transparent;
    color: var(--wp-text-disabled);
    cursor: not-allowed;
  }
}
</style>
